import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useDelayStore from '../store/delayStore';
import useUIStore from '../store/uiStore';
import { exportDelaysXlsx } from '../services/exportService';
import { downloadBlob } from '../utils/downloadFile';
import { getEmployees, getDepartments } from '../services/employeeService';
import { getProjects } from '../services/projectService';
import ScrollX from '../components/ScrollX';

const statusColors = {
  open: 'bg-gray-200 text-gray-800',
  escalated_7d: 'bg-amber-100 text-amber-800',
  escalated_15d: 'bg-red-100 text-red-800',
  closed: 'bg-green-100 text-green-800',
};

const statusLabels = {
  open: 'Open',
  escalated_7d: 'Escalated (7d)',
  escalated_15d: 'Escalated (15d)',
  closed: 'Closed',
};

const StatCard = ({ label, value }) => (
  <div className="glass-card p-4">
    <div className="text-sm text-gray-500">{label}</div>
    <div className="text-2xl font-bold text-gray-900">{value ?? '—'}</div>
  </div>
);

const DelayListPage = () => {
  const { delays, stats, isLoading, error, fetchDelays, fetchStats } = useDelayStore();
  const showToast = useUIStore((s) => s.showToast);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [supervisorFilter, setSupervisorFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');

  useEffect(() => {
    fetchDelays();
    fetchStats();
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    getProjects().then((res) => setProjects(res.data.projects || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const applyFilters = (overrides = {}) => {
    const next = {
      status: statusFilter,
      responsible_dept_id: deptFilter,
      responsible_supervisor_id: supervisorFilter,
      project_id: projectFilter,
      ...overrides,
    };
    const params = {};
    if (next.status) params.status = next.status;
    if (next.responsible_dept_id) params.responsible_dept_id = next.responsible_dept_id;
    if (next.responsible_supervisor_id) params.responsible_supervisor_id = next.responsible_supervisor_id;
    if (next.project_id) params.project_id = next.project_id;
    fetchDelays(params);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
    applyFilters({ status: e.target.value });
  };

  const handleDeptChange = (e) => {
    setDeptFilter(e.target.value);
    setSupervisorFilter('');
    applyFilters({ responsible_dept_id: e.target.value, responsible_supervisor_id: '' });
  };

  const handleSupervisorChange = (e) => {
    setSupervisorFilter(e.target.value);
    setDeptFilter('');
    applyFilters({ responsible_supervisor_id: e.target.value, responsible_dept_id: '' });
  };

  const handleProjectChange = (e) => {
    setProjectFilter(e.target.value);
    applyFilters({ project_id: e.target.value });
  };

  const handleExport = async () => {
    try {
      const res = await exportDelaysXlsx();
      downloadBlob(res.data, 'delay_register.xlsx');
    } catch (err) {
      showToast('Failed to export', 'error');
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-white">Delays</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={statusFilter}
              onChange={handleStatusChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="escalated_7d">Escalated (7d)</option>
              <option value="escalated_15d">Escalated (15d)</option>
              <option value="closed">Closed</option>
            </select>
            <select
              value={deptFilter}
              onChange={handleDeptChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All departments</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <select
              value={supervisorFilter}
              onChange={handleSupervisorChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All individuals</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
            <select
              value={projectFilter}
              onChange={handleProjectChange}
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            >
              <option value="">All projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <button onClick={handleExport} className="text-white hover:underline text-xs font-semibold">
              Export Excel
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Open" value={stats.open} />
            <StatCard label="Escalated (7d)" value={stats.escalated_7d} />
            <StatCard label="Escalated (15d)" value={stats.escalated_15d} />
            <StatCard label="Closed This Month" value={stats.closed_this_month} />
          </div>
        )}

        <div className="glass-card">
          {isLoading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : delays.length === 0 ? (
            <p className="text-gray-500 text-sm p-5">No delays found.</p>
          ) : (
            <ScrollX>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-2 font-medium">Punch Point</th>
                  <th className="px-5 py-2 font-medium">Current Delay</th>
                  <th className="px-5 py-2 font-medium">Responsible</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                  <th className="px-5 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {delays.map((d) => (
                  <tr key={d.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-5 py-3">#{d.punch_point_id}</td>
                    <td className="px-5 py-3">
                      <span className={d.status !== 'closed' ? 'text-red-600 font-semibold' : ''}>
                        {d.current_delay_days} day{d.current_delay_days === 1 ? '' : 's'}
                      </span>
                      {d.is_external_cause ? ' (external cause)' : ''}
                    </td>
                    <td className="px-5 py-3 text-gray-500">
                      {d.responsible_supervisor_name || d.responsible_dept_name || '—'}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${statusColors[d.status]}`}>
                        {statusLabels[d.status] || d.status}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Link to={`/delays/${d.id}`} className="text-komal-blue hover:underline text-xs font-semibold">
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelayListPage;
